// @ts-nocheck
import { IS_NODE } from 'shared/consts/env'
import {
  DisjunctiveFacet,
  DisjunctiveFacetsRefinements,
  FacetName,
  RangeFacetName,
  ResultsStateFacetsData,
  ResultsStateReact,
  SearchRange,
  SearchState,
  ToggleFacetName,
} from 'types/search'

export const convertToResultsStateFacetsData = (
  facets: DisjunctiveFacet[]
): ResultsStateFacetsData =>
  Array.isArray(facets)
    ? Object.fromEntries(facets.map(item => [item.name, item.data]))
    : {}

/**
 * Format the filter value in a range
 *
 * @example returns `2-5` for rating
 * @example `2-` and `-3` are also valid and expected
 */
export const formatRange = (filter: SearchRange): string =>
  [filter?.min || '', filter?.max || ''].join('-')

type DataLayerDisjunctiveFacet<T = FacetName> = {
  filterName: T
  filter: string
  hits: number
}

/**
 * Some facets have an extra logic. The `shippingOptions` consists of two other facets:
 * filterAttributes/ABO and nowProductGroup/sameday_nextday.
 * Thus, these two original facets are not shown, but produce custom `shippingOptions` filter
 * (NB: filterAttributes is shown, but it doesn't display ABO option)
 */
const getToggleFacetHitsNumber = (
  facets: ResultsStateFacetsData,
  name: ToggleFacetName
): number => {
  switch (name) {
    case 'filterAttributes':
      return facets[name]?.['ABO'] || 0

    case 'nowProductGroup':
      return facets[name]?.['sameday_nextday'] || 0

    default:
      return 0
  }
}

const shouldHandleToggleFacet = (name: FacetName): name is ToggleFacetName =>
  name === 'filterAttributes' || name === 'nowProductGroup'

/**
 * Parse DisjunctiveFacets (filter with checkbox) to dataLayer Object
 */
export const getDataLayerDisjunctiveFacets = (
  facets: ResultsStateFacetsData,
  refinements: DisjunctiveFacetsRefinements = {}
): DataLayerDisjunctiveFacet[] =>
  Object.keys(refinements)
    .map(facetName => {
      let filters: string[]
      const filterValue = refinements[facetName]

      if (!filterValue) {
        filters = []
      } else if (!Array.isArray(filterValue)) {
        filters = [filterValue]
      } else {
        filters = filterValue
      }

      return filters.reduce(
        (filterData: DataLayerDisjunctiveFacet[], filter: string) => {
          const hitsNumber = shouldHandleToggleFacet(facetName)
            ? getToggleFacetHitsNumber(facets, facetName)
            : facets?.[facetName]?.[filter] || 0

          return filterData.concat({
            filterName: facetName,
            filter,
            hits: hitsNumber,
          })
        },
        []
      )
    })
    .flat()

export const getRangeDataLayerDisjunctiveFacets = (
  refinements: SearchState['range'] = {},
  nbHits?: number | null
): DataLayerDisjunctiveFacet[] =>
  Object.keys(refinements)
    .map(filterName => {
      const range = refinements[filterName] || {}

      if (range.min === undefined && range.max === undefined) {
        return null
      }

      return {
        filterName,
        hits: nbHits || 0,
        filter: formatRange(range),
      }
    })
    .filter((i): i is DataLayerDisjunctiveFacet<RangeFacetName> => Boolean(i))

/**
 * Push the filter data to the dataLayer object
 */
const pushActiveFiltersGTM = (data: DataLayerRecord) => {
  if (!IS_NODE) {
    // eslint-disable-next-line no-restricted-globals
    window.dataLayer?.push?.(data)
  }
}

export const updateGTM = (
  searchState: SearchState,
  searchResults: ResultsStateReact
): void => {
  if (!searchResults || !searchState) {
    return
  }

  const { nbHits = null, disjunctiveFacets = [] } = searchResults
  const {
    refinementList,
    range,
    menu,
    toggle,
    hitsPerPage: pageSize,
  } = searchState

  const rangeDataLayerFacets = getRangeDataLayerDisjunctiveFacets(range, nbHits)
  const dataLayerFacets = getDataLayerDisjunctiveFacets(
    convertToResultsStateFacetsData(<DisjunctiveFacet[]>disjunctiveFacets),
    {
      ...refinementList,
      ...toggle,
      ...menu,
    }
  )

  const filterData = [...rangeDataLayerFacets, ...dataLayerFacets]

  const algolia: DataLayerRecord['algolia'] = {
    activeFilters: filterData,
    nbHits,
  }

  if (pageSize) {
    algolia.pageSize = pageSize
  }

  pushActiveFiltersGTM(<DataLayerRecord>{
    filterData,
    algolia,
  })
}
